// src/pages/Dashboard/components/MarkMoment.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. The salesperson half of marks: the moment on Today when
//               somebody opens one their manager gave them.
//
// THE MOMENT SOMEBODY FINDS OUT THEY WERE NOTICED.
//
// Shown once. It sits at the top of Today until they tap "Keep it", which
// stamps seen_at (the one column 0046 lets them write) and it folds away. A
// mark that greeted somebody every morning forever would stop meaning anything
// by Thursday.
//
// IT SAYS WHO GAVE IT, WHAT THEY SAW AND WHY IT MATTERS. The name matters most:
// Gallup's most memorable recognition comes from the person's own manager, so
// the manager's name leads rather than "You earned a mark". Then the manager's
// own words if they wrote any, then the one line from content/marks.js on why
// that behaviour matters.
//
// NO COUNT. Not "your third mark", not "4 this month". A tally turns
// recognition into a score to chase. tests/marks.test.mjs reads this file.
//
// Motion is one soft rise on arrival, and none at all for anybody who has
// asked their device for reduced motion.

import { useEffect, useState } from 'react'
import { Box, HStack, VStack, Text, Button } from '@chakra-ui/react'
// Chakra v2 does not re-export keyframes from this entry, emotion does.
import { keyframes } from '@emotion/react'
import Medallion from '../../../components/ui/Medallion.jsx'
import { markByKey } from '../../../content/marks'
import { firstNameFor } from '../../../lib/names.js'
import { listFor, listPeople, markSeen, unseenFor, dayLabel } from '../../../lib/recognitions'

const rise = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`

export default function MarkMoment({ userId }) {
  const [unseen, setUnseen] = useState([])
  const [givers, setGivers] = useState({})
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    if (!userId) return
    let live = true
    ;(async () => {
      const { data } = await listFor(userId, 20)
      // A failed read shows nothing. A missing moment is a thing nobody
      // notices; a wrong one is a thing somebody screenshots.
      if (!live || !data) return
      const fresh = unseenFor(userId, data).slice(0, 3)
      setUnseen(fresh)
      if (fresh.length) {
        const who = await listPeople(fresh.map((r) => r.given_by))
        if (live && who.data) setGivers(Object.fromEntries(who.data.map((u) => [u.id, u])))
      }
    })()
    return () => {
      live = false
    }
  }, [userId])

  if (unseen.length === 0) return null

  const keep = async () => {
    setClosing(true)
    await markSeen(unseen.map((r) => r.id))
    setUnseen([])
  }

  return <MarkMomentView unseen={unseen} givers={givers} closing={closing} onKeep={keep} />
}

// The card itself, from data it is handed, so the dev preview can draw it.
export function MarkMomentView({ unseen, givers = {}, closing = false, onKeep }) {
  if (!unseen || unseen.length === 0) return null
  const keep = () => onKeep?.()

  return (
    <Box
      bg="card"
      border="1px solid"
      borderColor="line"
      borderRadius="cardLg"
      p={{ base: 5, md: 6 }}
      position="relative"
      overflow="hidden"
      opacity={closing ? 0.6 : 1}
      animation={`${rise} 420ms ease-out both`}
      sx={{ '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
    >
      {/* One gold hairline across the top, the only place gold appears as a
          rule anywhere in the product. */}
      <Box position="absolute" top={0} left={0} right={0} h="2px" bg="gold" opacity={0.7} />

      <Text textStyle="eyebrow" color="gold" mb={4}>
        {unseen.length === 1 ? 'SOMEBODY NOTICED' : 'YOU WERE NOTICED'}
      </Text>

      <VStack align="stretch" spacing={5}>
        {unseen.map((r) => {
          const m = markByKey(r.mark)
          const giver = firstNameFor(givers[r.given_by]) || 'Your manager'
          return (
            <HStack key={r.id} align="flex-start" spacing={4}>
              <Medallion mark={r.mark} size={unseen.length === 1 ? 64 : 48} />
              <Box minW={0} flex={1}>
                <Text fontSize={unseen.length === 1 ? 'xl' : 'lg'} fontWeight={600} letterSpacing="-0.015em" lineHeight={1.25}>
                  {m?.name || 'Recognised'}
                </Text>
                <Text fontSize="body-sm" color="inkMuted" mt={0.5}>
                  {giver} saw it {dayLabel(r.created_at)}
                  {m?.step ? `, Step ${m.step}` : ''}.
                </Text>
                {r.note && (
                  <Text fontSize="body" mt={2} lineHeight={1.5} maxW="measure">
                    “{r.note}”
                  </Text>
                )}
                {m?.cheer && (
                  <Text fontSize="body-sm" color="inkMuted" mt={2} lineHeight={1.5} maxW="measure">
                    {m.cheer}
                  </Text>
                )}
              </Box>
            </HStack>
          )
        })}
      </VStack>

      <HStack mt={5} spacing={3}>
        <Button onClick={keep} isLoading={closing} size="md">
          {unseen.length === 1 ? 'Keep it' : 'Keep them'}
        </Button>
      </HStack>
    </Box>
  )
}
